import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FILE_UPLOADER } from '../../../../../componentes/file-uploader/file-uploader.component';
import { CustomValidators } from '../../../../../validators/CustomValidators';
import {
  SOLICITACAO_DOCUMENTO_DYNAMIC_FIELDS,
  SOLICITACAO_DOCUMENTO_TIPO_OPTIONS,
  SolicitacaoDocumentoContext,
  SolicitacaoDocumentoEspecifico,
  SolicitacaoDocumentoField,
  SolicitacaoDocumentoFinalidade,
  SolicitacaoDocumentoOption,
  SolicitacaoDocumentoTipo,
  getSolicitacaoDocumentoContext
} from './solicitacao-documento.config';

@Component({
  selector: 'app-solicitacao-documento',
  templateUrl: './solicitacao-documento.component.html',
  styleUrls: ['./solicitacao-documento.component.css']
})
export class SolicitacaoDocumentoComponent implements OnInit, OnDestroy {

  @ViewChild(FILE_UPLOADER, { static: false }) fileUploader: any;
  @Input() formGroup!: FormGroup;
  @Input() formSubmitted = false;
  @Input() readonly!: boolean;
  @Input() arquivosSalvos!: any[];

  readonly documentType = SolicitacaoDocumentoTipo;
  readonly documentField = SolicitacaoDocumentoField;
  readonly opcoesTipoDocumento = SOLICITACAO_DOCUMENTO_TIPO_OPTIONS;

  private readonly destroy$ = new Subject<void>();
  private readonly fieldValidators: Partial<Record<SolicitacaoDocumentoField, ValidatorFn[]>> = {
    [SolicitacaoDocumentoField.DOCUMENTO_ESPECIFICO]: [Validators.required],
    [SolicitacaoDocumentoField.NOME_COMPLETO]: [Validators.required],
    [SolicitacaoDocumentoField.CPF]: [Validators.required, CustomValidators.cpf],
    [SolicitacaoDocumentoField.MATRICULA]: [Validators.required],
    [SolicitacaoDocumentoField.TELEFONE]: [Validators.required, CustomValidators.celular],
    [SolicitacaoDocumentoField.EMAIL]: [Validators.required, Validators.email],
    [SolicitacaoDocumentoField.EMAIL_RESPOSTA]: [Validators.required, Validators.email],
    [SolicitacaoDocumentoField.ORGAO_DESTINO]: [Validators.required],
    [SolicitacaoDocumentoField.FINALIDADE]: [Validators.required],
    [SolicitacaoDocumentoField.DESTINO]: [Validators.required],
    [SolicitacaoDocumentoField.OBJETO]: [Validators.required],
    [SolicitacaoDocumentoField.AGENDAMENTO]: [Validators.required],
    [SolicitacaoDocumentoField.ESPECIFICAR]: [Validators.required],
    [SolicitacaoDocumentoField.DESCRICAO_DETALHADA]: [Validators.required],
    [SolicitacaoDocumentoField.ARQUIVO]: [Validators.required]
  };

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.monitorarEstadoFormulario();
    this.aplicarEstadoDinamico();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get arquivoControl(): FormArray {
    return this.formGroup.get(SolicitacaoDocumentoField.ARQUIVO) as FormArray;
  }

  get tipoDocumentoDesejadoControl(): FormControl {
    return this.formGroup.get('tipoDocumentoDesejado') as FormControl;
  }

  get documentoEspecificoControl(): FormControl {
    return this.formGroup.get(SolicitacaoDocumentoField.DOCUMENTO_ESPECIFICO) as FormControl;
  }

  get finalidadeControl(): FormControl {
    return this.formGroup.get(SolicitacaoDocumentoField.FINALIDADE) as FormControl;
  }

  get documentoContext(): SolicitacaoDocumentoContext {
    return getSolicitacaoDocumentoContext(this.formGroup.getRawValue());
  }

  get opcoesDocumentoEspecifico(): SolicitacaoDocumentoOption<SolicitacaoDocumentoEspecifico>[] {
    return this.documentoContext.documentoEspecificoOptions;
  }

  get opcoesDocumentoEspecificoRadio(): { val: string; label: string }[] {
    return this.opcoesDocumentoEspecifico.map(option => ({
      val: option.value,
      label: option.nome
    }));
  }

  get opcoesFinalidade(): SolicitacaoDocumentoOption<SolicitacaoDocumentoFinalidade>[] {
    return this.documentoContext.finalidadeOptions;
  }

  get isAttachmentRequired(): boolean {
    return this.documentoContext.attachmentRequired;
  }

  get shouldShowUpload(): boolean {
    return !this.readonly && this.isSelectedDocumentType(SolicitacaoDocumentoTipo.ATO_APOSENTADORIA);
  }

  onArquivosChange(arquivos: any[]) {
    this.arquivoControl.clear();

    if (arquivos && arquivos.length) {
      arquivos.forEach(arq => {
        this.arquivoControl.push(this.fb.group({
          nomeArquivo: arq.nomeArquivo,
          conteudo: arq.conteudo
        }));
      });
    }

    this.arquivoControl.markAsTouched();
    this.arquivoControl.updateValueAndValidity();
    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }

  isSelectedDocumentType(tipo: SolicitacaoDocumentoTipo): boolean {
    return this.tipoDocumentoDesejadoControl && this.tipoDocumentoDesejadoControl.value === tipo;
  }

  isFieldVisible(field: SolicitacaoDocumentoField): boolean {
    return this.documentoContext.visibleFields.indexOf(field) !== -1;
  }

  private monitorarEstadoFormulario() {
    this.tipoDocumentoDesejadoControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetarAnexos();
        this.aplicarEstadoDinamico();
      });

    this.documentoEspecificoControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.aplicarEstadoDinamico());

    this.finalidadeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.aplicarEstadoDinamico());
  }

  private aplicarEstadoDinamico() {
    const contexto = this.documentoContext;

    SOLICITACAO_DOCUMENTO_DYNAMIC_FIELDS.forEach(field => {
      const control = this.formGroup.get(field);

      if (!control) {
        return;
      }

      if (this.isFieldVisible(field)) {
        this.aplicarValidators(control, contexto.requiredFields.indexOf(field) !== -1 ? this.fieldValidators[field] || [] : []);
        return;
      }

      this.limparControle(control);
      this.aplicarValidators(control, []);
    });

    this.aplicarValidators(
      this.arquivoControl,
      contexto.attachmentRequired ? this.fieldValidators[SolicitacaoDocumentoField.ARQUIVO] || [] : []
    );

    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }

  private aplicarValidators(control: AbstractControl, validators: ValidatorFn[]) {
    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private limparControle(control: AbstractControl) {
    if (control instanceof FormArray) {
      control.clear();
    } else {
      control.reset('', { emitEvent: false });
    }

    control.markAsPristine();
    control.markAsUntouched();
  }

  private resetarAnexos() {
    this.arquivoControl.clear();
    this.arquivoControl.markAsPristine();
    this.arquivoControl.markAsUntouched();

    if (this.fileUploader) {
      if (this.fileUploader.uploader && typeof this.fileUploader.uploader.clearQueue === 'function') {
        this.fileUploader.uploader.clearQueue();
      }

      if (this.fileUploader.mapFilesBase64 && typeof this.fileUploader.mapFilesBase64.clear === 'function') {
        this.fileUploader.mapFilesBase64.clear();
      }

      if (typeof this.fileUploader.emitirArquivos === 'function') {
        this.fileUploader.emitirArquivos();
      }
    }

    this.arquivoControl.updateValueAndValidity({ emitEvent: false });
  }
}
